import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import { ICellRendererParams } from '@ag-grid-community/core/dist/cjs/rendering/cellRenderers/iCellRenderer'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import i18n from '@/lang/i18n'
import { ScientificAnalysisDatasource } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource'
import { ScientificAnalysisEntitySample } from '@/modules/scientific-analysis-module/services/scientific-analysis-dto-sample'
import { scientificAnalysisService } from '@/modules/scientific-analysis-module/services/scientific-analysis-services'
import { Nullable } from '@/shared/types/app.types'
import { ColDef } from '@ag-grid-enterprise/all-modules'
import CellGridRichText from '@/components/common/CellGridRichText.vue'
import { InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'
import { ScientificAnalysisCaseDatasource } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource-case'
import { ScientificAnalysisPatientDatasource } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource-patient'

export class ScientificAnalysisSampleDatasource extends ScientificAnalysisDatasource<ScientificAnalysisEntitySample> {
    public static getEntityGridColumns(selector: string = '', hideAll: boolean = false): (ColDef | ExportedColDef | ExportedColGroupDef) {
        return {
            headerName: i18n.tc(`scientificAnalysis.grid.results.sample.header`),
            children: [
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.sampleNumber`),
                    exportField: `${selector}label`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}label`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.localization`),
                    exportField: `${selector}topographicCode.description`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}topographicCode.description`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.sampleType`),
                    exportField: `${selector}sampleType.description`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}sampleType.description`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.GROSSING_SAMPLE_STATUS,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.status`),
                    exportField: `${selector}status.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}status`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.firstGrossingDate`),
                    exportField: `${selector}firstSampleGrossing.grossingDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}firstSampleGrossing.grossingDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.responsibleRegrossing`),
                    exportField: `${selector}responsibleRegrossing.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}responsibleRegrossing.fullName`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.specialist`),
                    exportField: `${selector}specialist.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}specialist.fullName`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.macrodescription`),
                    exportField: `${selector}macro`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}macro`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    width: 500,
                    cellStyle: { 'white-space': 'normal', 'overflow-y': 'auto' },
                    cellRendererFramework: CellGridRichText,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.diagnosis`),
                    exportField: `${selector}morphologicCode`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}morphologicCode`,
                    suppressSizeToFit: true,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    width: 500,
                    cellStyle: { 'white-space': 'normal', 'overflow-y': 'auto' },
                    cellRendererFramework: CellGridRichText,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.codifications`),
                    exportDisabled: true,
                    field: `${selector}codifications`,
                    minWidth: 200,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    cellRenderer: ({ data }: ICellRendererParams) => {
                        let value = ``
                        if (data?.codifications?.length > 0) {
                            let codification
                            for (codification of data.codifications) {
                                if (codification.topographicCode) {
                                    if (value.length > 0) {
                                        value += ` ,`
                                    }
                                    value += codification.topographicCode.code + `|` + codification.topographicCode.description
                                }

                                if (codification.morphologicCode) {
                                    if (value.length > 0) {
                                        value += ` ,`
                                    }
                                    value += codification.morphologicCode.code + `|` + codification.morphologicCode.description
                                }
                            }
                        }
                        return value
                    },
                    hide: hideAll

                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.procedure`),
                    exportField: `${selector}procedure.description`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}procedure.description`,
                    minWidth: 200,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.mostLikelyMorphologicCode`),
                    exportField: `${selector}mostLikelyMorphologicCode.description`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}mostLikelyMorphologicCode.description`,
                    minWidth: 200,
                    flex: 1,
                    hide: true,
                    sortable: false,
                    filter: false
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.observations`),
                    exportField: `${selector}observations`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}observations`,
                    minWidth: 200,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.conservation`),
                    exportField: `${selector}conservation`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}conservation`,
                    minWidth: 200,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.samplingDate`),
                    exportField: `${selector}samplingDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}samplingDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.regrossingLastDate`),
                    exportField: `${selector}regrossingLastDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}regrossingLastDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.wasteDate`),
                    exportField: `${selector}wasteDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}wasteDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.partialEmbedding`),
                    exportField: `${selector}partialEmbedding`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}partialEmbedding`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.sampleProcess`),
                    exportField: `${selector}sampleProcess.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}sampleProcess.name`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.SAMPLE_VIRTUAL_SHELF,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.virtualShelf`),
                    exportField: `${selector}virtualShelf.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}virtualShelf.id`,
                    sortable: false,
                    filter: false,
                    width: 110,
                    suppressSizeToFit: true,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.fixer`),
                    exportField: `${selector}fixer.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}fixer.name`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.wasteTechnician`),
                    exportField: `${selector}wasteTechnician.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}wasteTechnician.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.grossingCoordinator`),
                    exportField: `${selector}grossingCoordinator.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}grossingCoordinator.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.grossingTechController`),
                    exportField: `${selector}grossingTechController.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}grossingTechController.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.grossingTutor`),
                    exportField: `${selector}grossingTutor.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}grossingTutor.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.micro`),
                    exportField: `${selector}micro`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}micro`,
                    sortable: false,
                    filter: false,
                    hide: true,
                    minWidth: 200,
                    width: 500,
                    cellStyle: { 'white-space': 'normal', 'overflow-y': 'auto' },
                    cellRendererFramework: CellGridRichText

                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.additionalReport`),
                    exportField: `${selector}additionalReport`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}additionalReport`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    width: 500,
                    cellStyle: { 'white-space': 'normal', 'overflow-y': 'auto' },
                    cellRendererFramework: CellGridRichText,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.urvdescription`),
                    exportField: `${selector}valuableConceptURV.description`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}valuableConceptURV.description`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.URV`),
                    exportField: `${selector}valuableConceptURV.totalValue`,
                    exportType: AgGridExportType.FLOAT,
                    field: `${selector}valuableConceptURV.totalValue`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.totalURV`),
                    exportField: `${selector}totalURV`,
                    exportType: AgGridExportType.DOUBLE,
                    field: `${selector}totalURV`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.UCL`),
                    exportField: `${selector}valuableConceptUCL.totalValue`,
                    exportType: AgGridExportType.FLOAT,
                    field: `${selector}valuableConceptUCL.totalValue`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.totalUCL`),
                    exportField: `${selector}totalUCL`,
                    exportType: AgGridExportType.DOUBLE,
                    field: `${selector}totalUCL`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.hasRevisions`),
                    exportField: `${selector}hasRevisions`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}hasRevisions`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.specialPreserve`),
                    exportField: `${selector}specialPreserve`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}specialPreserve`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.hasAttachments`),
                    exportField: `${selector}hasAttachments`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}hasAttachments`,
                    sortable: false,
                    filter: false,
                    headerComponentParams: {
                        template: buildIconHeaderTemplate(`far fa-paperclip`)
                    },
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.hasIncidents`),
                    exportField: `${selector}hasIncidents`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}hasIncidents`,
                    sortable: false,
                    filter: false,
                    headerComponentParams: {
                        template: buildIconHeaderTemplate(`far fa-exclamation-triangle`)
                    },
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.sample.sampleTag`),
                    exportField: `${selector}sampleTag`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}sampleTag`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                }
            ]
        }
    }

    public getGridColumns(selector: string = ''): (ColDef | ExportedColDef | ExportedColGroupDef)[] {
        return [
            { type: AgGridColumnType.CUSTOM_CHECKBOX },
            ScientificAnalysisSampleDatasource.getEntityGridColumns(),
            ScientificAnalysisCaseDatasource.getEntityGridColumns(`caseStudy.`, true),
            ScientificAnalysisPatientDatasource.getEntityGridColumns(`caseStudy.patient.`, true)
        ]
    }

    public async paginator(_filterModel: CommonFilterModel, params: CommonParams, scrollId: string, totalElements: number): Promise<InfinitePaginatorResponse<ScientificAnalysisEntitySample>> {
        const response: InfinitePaginatorResponse<ScientificAnalysisEntitySample> = await scientificAnalysisService.search(this.scientificAnalysis, params, scrollId, totalElements) as InfinitePaginatorResponse<ScientificAnalysisEntitySample>
        this.setTotalElements(response.scrollPage.totalElements)
        return {
            content: response.content,
            scrollPage: {
                scrollId: encodeURIComponent(JSON.stringify(response?.scrollPage?.scrollId)),
                size: response.scrollPage.size,
                totalElements: response.scrollPage.totalElements
            }
        }
    }

    public getPatAndLabId(selectedRow: ScientificAnalysisEntitySample): [number, number] {
        return [selectedRow.caseStudy.patient.id, selectedRow.caseStudy.laboratoryId]
    }

    public getLabAndCaseId(selectedRow: ScientificAnalysisEntitySample): [number, Nullable<number>] {
        return [selectedRow.caseStudy.laboratoryId, selectedRow.caseStudy.id]
    }

    public getHTMLFieldsList(data: ScientificAnalysisEntitySample): string[] {
        const fields = [data.micro, data.macro, data.morphologicCode, data.additionalReport]
        return fields.filter(fieldValue => fieldValue)
    }
}
